import {
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";

import { salesApi } from "../client.js";

/**
 * Per-campaign results: contacts + emails, orgs + coverage, spend, and
 * promoted-lead outcomes.
 *
 * Two presentation rules the numbers depend on:
 *
 *  - **Verified and guessed emails are shown apart.** A pattern-guessed
 *    address is not a webinar invitation, and a single "emails" figure would
 *    inflate the exact number the campaign is judged on.
 *  - **"No contacts yet" is never rendered as $0.00 per contact.** The server
 *    sends null for that case; showing a zero would read as free when the
 *    truth is "spent money, produced nothing to divide by".
 *  - **Spend the campaign can't account for is shown, not hidden.** Until
 *    2026-09-05 the dig pass — the expensive half of harvesting — sent its LLM
 *    calls out untagged, so this card confidently showed tens of dollars while
 *    the harvester had spent hundreds. The fix is upstream (the dig now tags
 *    itself), but historical rows can never be attributed, so the server sends
 *    `spend.unattributed_usd` and the card says so under the figure.
 */
export function CampaignReport({ slug }: { slug: string }): JSX.Element {
  const { data, isPending, error } = useQuery({
    queryKey: ["sales", "campaigns", slug, "report"],
    queryFn: () => salesApi.getCampaignReport(slug),
  });

  if (isPending) return <Skeleton variant="rounded" height={220} />;
  if (error) {
    return (
      <Typography sx={{ color: "error.main" }}>
        Couldn’t load results. {(error as Error).message}
      </Typography>
    );
  }
  if (!data) return <></>;

  const { contacts, orgs, coverage, spend, leads } = data;

  return (
    <Stack spacing={2} data-testid={`campaign-report-${slug}`}>
      <Stack direction="row" gap={2} flexWrap="wrap">
        <Stat
          label="Contacts"
          value={contacts.total}
          hint={`${contacts.with_linkedin} with LinkedIn`}
        />
        <Stat
          label="Verified emails"
          value={contacts.verified_email}
          hint={`+${contacts.guessed_email} guessed (unconfirmed)`}
          emphasis
        />
        <Stat label="Organisations" value={orgs.total} hint={`${orgs.with_contacts} yielded a contact`} />
        <Stat
          label="Spend"
          value={`$${spend.cost_usd.toFixed(2)}`}
          hint={
            data.cost_per_verified_email_usd === null
              ? "no verified email yet"
              : `$${data.cost_per_verified_email_usd.toFixed(4)} per verified email`
          }
          note={
            spend.unattributed_usd
              ? `+ $${spend.unattributed_usd.toFixed(2)} harvester spend tagged to no campaign`
              : undefined
          }
        />
        <Stat
          label="Promoted to leads"
          value={leads.promoted}
          hint={`${leads.rejected} rejected · ${leads.exists_in_crm} already in CRM`}
        />
      </Stack>

      <Card variant="outlined">
        <CardContent>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Coverage</Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, coverage.percent_complete)}
            sx={{ height: 8, borderRadius: 1, mb: 1 }}
          />
          <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
            {coverage.cells_total === 0
              ? "Not started — no target cells seeded yet."
              : `${coverage.percent_complete}% of the target matrix swept — ` +
                `${coverage.cells_done} done, ${coverage.cells_pending} pending, ` +
                `${coverage.cells_running} running, ${coverage.cells_failed} failed.`}
          </Typography>
        </CardContent>
      </Card>

      {data.per_vertical.length > 0 && (
        <Card variant="outlined">
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Vertical</TableCell>
                    <TableCell align="right">Orgs</TableCell>
                    <TableCell align="right">Contacts</TableCell>
                    <TableCell align="right">Emails</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.per_vertical.map((r) => (
                    <TableRow key={r.vertical}>
                      <TableCell>{r.vertical}</TableCell>
                      <TableCell align="right">{r.orgs}</TableCell>
                      <TableCell align="right">{r.contacts}</TableCell>
                      <TableCell align="right">{r.emails}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
        </Card>
      )}

      {Object.keys(data.contacts_by_seniority).length > 0 && (
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            Seniority:
          </Typography>
          {Object.entries(data.contacts_by_seniority)
            .sort((a, b) => b[1] - a[1])
            .map(([level, n]) => (
              <Chip key={level} size="small" label={`${level}: ${n}`} />
            ))}
        </Stack>
      )}
    </Stack>
  );
}

function Stat({
  label,
  value,
  hint,
  note,
  emphasis,
}: {
  label: string;
  value: number | string;
  hint?: string;
  /** A caveat about the figure itself — rendered in the warning colour, below
   *  the hint. Used for spend this campaign genuinely cannot account for.
   *  Explicitly `| undefined`: the caller passes `undefined` when there is no
   *  caveat, which `exactOptionalPropertyTypes` rejects on a bare `note?`. */
  note?: string | undefined;
  emphasis?: boolean;
}): JSX.Element {
  return (
    <Card variant="outlined" sx={{ minWidth: 168, flex: "1 1 168px" }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{label}</Typography>
        <Typography
          sx={{
            fontSize: 24,
            fontWeight: 600,
            color: emphasis ? "success.main" : "text.primary",
          }}
        >
          {value}
        </Typography>
        {hint && (
          <Typography sx={{ fontSize: 12, color: "text.disabled" }}>{hint}</Typography>
        )}
        {note && (
          <Typography sx={{ fontSize: 12, color: "warning.main" }}>{note}</Typography>
        )}
      </CardContent>
    </Card>
  );
}
